import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";

import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";

import Layout from "../layouts/Layout";

import {
  API_BASE_URL,
  SKIN_THEMES,
} from "../layouts/constants";

/* =========================================================
   SKIN
========================================================= */

function useSkin() {
  const skinId =
    localStorage.getItem("skinTheme") || "navy";

  return (
    SKIN_THEMES.find((s) => s.id === skinId) ||
    SKIN_THEMES[0]
  );
}

/* =========================================================
   TOKEN
========================================================= */

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken")
  );
}

/* =========================================================
   API URL
========================================================= */

function api(path) {
  const base = API_BASE_URL.replace(/\/$/, "");

  if (
    base.endsWith("/api") &&
    path.startsWith("/api/")
  ) {
    return `${base}${path.replace(/^\/api/, "")}`;
  }

  return `${base}${path}`;
}

/* =========================================================
   MEDIA
========================================================= */

const MEDIA_BASE =
  process.env.REACT_APP_MEDIA_BASE_URL ||
  "https://rfs-sentinel.eblackdog.net";

function getMediaUrl(path) {
  if (!path) return null;

  let p = String(path).trim();

  if (
    !p ||
    p === "-" ||
    p.toLowerCase() === "null" ||
    p.toLowerCase() === "undefined"
  ) {
    return null;
  }

  if (/^https?:\/\//i.test(p)) {
    return p;
  }

  p = p.replace(/\\/g, "/");

  const uploadsIndex =
    p.toLowerCase().indexOf("/uploads/");

  if (uploadsIndex !== -1) {
    p = p.substring(uploadsIndex);
  }

  if (
    p.toLowerCase().includes("server/uploads/")
  ) {
    const idx =
      p.toLowerCase().indexOf("server/uploads/");

    p =
      "/" +
      p.substring(
        idx + "server".length
      );
  }

  if (!p.startsWith("/")) {
    p = `/${p}`;
  }

  return `${MEDIA_BASE}${p}`;
}

function isMediaField(key) {
  const k =
    String(key || "").toLowerCase();

  return (
    k === "image_path" ||
    k === "media_path" ||
    k.includes("image") ||
    k.includes("media") ||
    k.includes("selfie") ||
    k.includes("video")
  );
}

function isImageUrl(url) {
  if (!url) return false;

  const u =
    String(url).toLowerCase();

  return (
    u.includes(".jpg") ||
    u.includes(".jpeg") ||
    u.includes(".png") ||
    u.includes(".gif") ||
    u.includes(".webp") ||
    u.includes(".bmp")
  );
}

/* =========================================================
   VALUE FORMATTING
========================================================= */

function formatValue(value, key) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    if (
      key === "place_details" ||
      key === "place_name" ||
      key === "route_name" ||
      key === "route_location"
    ) {
      return "Not assigned";
    }

    return "-";
  }

  if (isMediaField(key)) {
    return value;
  }

  const keyLower =
    String(key || "").toLowerCase();

  /* =====================================================
     RFID ORIGINAL VALUES
     IMPORTANT:
     Do not convert these with browser timezone.
  ===================================================== */

  if (keyLower === "scan_time") {
    return String(value);
  }

  if (keyLower === "scan_datetime") {
    return String(value);
  }

  if (keyLower === "scan_date") {
    const str = String(value).trim();

    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
      const [year, month, day] = str.split("-");

      return `${day}/${month}/${year}`;
    }

    return str;
  }

  /* =====================================================
     Attendance first/last
  ===================================================== */

  if (
    keyLower === "first_attendance_date" ||
    keyLower === "last_attendance_date"
  ) {
    try {
      const d = new Date(value);

      if (Number.isNaN(d.getTime())) {
        return "-";
      }

      const h = d.getUTCHours();
      const m = d.getUTCMinutes();
      const s = d.getUTCSeconds();
      const ms = d.getUTCMilliseconds();

      if (
        h === 0 &&
        m === 0 &&
        s === 0 &&
        ms === 0
      ) {
        return d.toLocaleDateString(
          "en-IN",
          {
            timeZone: "UTC",
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          }
        );
      }

      return d.toLocaleString(
        "en-IN",
        {
          timeZone: "Asia/Kolkata",
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        }
      );
    } catch {
      return String(value);
    }
  }

  /* =====================================================
     PURE DATE
  ===================================================== */

  if (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(
      value.trim()
    )
  ) {
    try {
      const d =
        new Date(
          value + "T00:00:00"
        );

      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleDateString(
          "en-IN",
          {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          }
        );
      }
    } catch {
      return String(value);
    }
  }

  /* =====================================================
     DATETIME
  ===================================================== */

  try {
    if (
      (typeof value === "string" ||
        value instanceof Date) &&
      !Number.isNaN(
        new Date(value).getTime()
      ) &&
      (
        String(value).includes("T") ||
        String(value).includes("Z") ||
        value instanceof Date ||
        keyLower.includes("_at") ||
        keyLower === "scanning_time"
      )
    ) {
      return new Date(
        value
      ).toLocaleString(
        "en-IN",
        {
          timeZone: "Asia/Kolkata",
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        }
      );
    }
  } catch {
    // fall through
  }

  return String(value);
}

/* =========================================================
   HELPERS
========================================================= */

function normalizeText(value) {
  return String(value || "").toLowerCase();
}

function getRowDate(row) {
  return (
    row.scan_date ||
    row.scan_datetime ||
    row.scanning_time ||
    row.created_at ||
    row.start_time ||
    row.end_time ||
    row.first_attendance_date ||
    row.last_attendance_date ||
    row.updated_at ||
    row.received_at ||
    null
  );
}

function groupCount(rows, key) {
  const map = {};

  rows.forEach((row) => {
    const value =
      row[key] ||
      "Unknown";

    map[value] =
      (map[value] || 0) + 1;
  });

  return Object.entries(map)
    .map(([name, value]) => ({
      name,
      value,
    }))
    .sort(
      (a, b) =>
        b.value - a.value
    );
}

function groupByDate(rows) {
  const map = {};

  rows.forEach((row) => {
    let rawDate =
      getRowDate(row);

    if (!rawDate) return;

    let dateKey;

    /*
     * RFID scan_date is yyyy-MM-dd.
     * Keep it as-is rather than converting timezone.
     */
    if (
      typeof rawDate === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(rawDate)
    ) {
      const [year, month, day] =
        rawDate.split("-");

      dateKey =
        `${day}/${month}/${year}`;
    } else {
      const dateObj =
        new Date(rawDate);

      if (
        Number.isNaN(
          dateObj.getTime()
        )
      ) {
        return;
      }

      dateKey =
        dateObj.toLocaleDateString(
          "en-IN",
          {
            timeZone:
              "Asia/Kolkata",
          }
        );
    }

    map[dateKey] =
      (map[dateKey] || 0) + 1;
  });

  return Object.entries(map).map(
    ([date, count]) => ({
      date,
      count,
    })
  );
}

function averageNumber(
  rows,
  key
) {
  const values = rows
    .map((row) =>
      Number(row[key])
    )
    .filter(
      (value) =>
        !Number.isNaN(value) &&
        value > 0
    );

  if (!values.length) {
    return 0;
  }

  return Math.round(
    values.reduce(
      (sum, value) =>
        sum + value,
      0
    ) / values.length
  );
}

function sumNumber(
  rows,
  key
) {
  return rows.reduce(
    (sum, row) => {
      const value =
        Number(row[key]);

      return (
        sum +
        (Number.isNaN(value)
          ? 0
          : value)
      );
    },
    0
  );
}

/* =========================================================
   SUMMARY CARDS
========================================================= */

function getReportSummary(
  reportType,
  rows
) {
  const total =
    rows.length;

  if (
    reportType ===
    "attendance"
  ) {
    return [
      {
        label: "Total Guards",
        value: total,
      },

      {
        label: "Active Guards",
        value: rows.filter(
          (r) =>
            normalizeText(
              r.active_status
            ) === "active"
        ).length,
      },

      {
        label:
          "Total Working Days",
        value: sumNumber(
          rows,
          "attendance_days"
        ),
      },

      {
        label:
          "Payroll Linked",
        value: rows.filter(
          (r) =>
            normalizeText(
              r.attendance_source
            ) === "payroll"
        ).length,
      },
    ];
  }

  if (
    reportType === "qr"
  ) {
    return [
      {
        label:
          "Total QR Scans",
        value: total,
      },

      {
        label:
          "Unique Guards",
        value:
          new Set(
            rows
              .map(
                (r) =>
                  r.guard_id
              )
              .filter(Boolean)
          ).size,
      },

      {
        label:
          "Routes Covered",
        value:
          new Set(
            rows
              .map(
                (r) =>
                  r.route_id
              )
              .filter(Boolean)
          ).size,
      },

      {
        label:
          "With Image",
        value: rows.filter(
          (r) =>
            normalizeText(
              r.has_scan_image
            ) === "yes" ||
            (
              r.image_path &&
              String(
                r.image_path
              ).trim()
            )
        ).length,
      },
    ];
  }

  /* =====================================================
     RFID - UPDATED
  ===================================================== */

  if (
    reportType === "rfid"
  ) {
    return [
      {
        label:
          "Total RFID Records",
        value: total,
      },

      {
        label:
          "Round Starts",
        value:
          rows.filter(
            (r) =>
              r.flag === "0S"
          ).length,
      },

      {
        label:
          "Unique Devices",
        value:
          new Set(
            rows
              .map(
                (r) =>
                  r.device_id
              )
              .filter(Boolean)
          ).size,
      },

      {
        label:
          "Unique Cards",
        value:
          new Set(
            rows
              .map(
                (r) =>
                  r.card_id
              )
              .filter(Boolean)
          ).size,
      },
    ];
  }

  if (
    reportType ===
    "incidents"
  ) {
    return [
      {
        label:
          "Total Incidents",
        value: total,
      },

      {
        label:
          "Open Incidents",
        value: rows.filter(
          (r) =>
            normalizeText(
              r.status
            ) === "open"
        ).length,
      },

      {
        label:
          "Closed Incidents",
        value: rows.filter(
          (r) =>
            normalizeText(
              r.status
            ) === "closed"
        ).length,
      },

      {
        label:
          "With Media",
        value: rows.filter(
          (r) =>
            normalizeText(
              r.has_media
            ) === "yes" ||
            (
              r.media_path &&
              String(
                r.media_path
              ).trim()
            ) ||
            (
              r.image_path &&
              String(
                r.image_path
              ).trim()
            )
        ).length,
      },
    ];
  }

  if (
    reportType ===
    "alerts"
  ) {
    return [
      {
        label:
          "Total Alerts",
        value: total,
      },

      {
        label:
          "Acknowledged",
        value: rows.filter(
          (r) =>
            normalizeText(
              r.alert_status
            ) ===
            "acknowledged"
        ).length,
      },

      {
        label:
          "Pending",
        value: rows.filter(
          (r) =>
            normalizeText(
              r.alert_status
            ) === "pending"
        ).length,
      },

      {
        label:
          "Severities",
        value:
          new Set(
            rows
              .map(
                (r) =>
                  r.severity
              )
              .filter(Boolean)
          ).size,
      },
    ];
  }

  if (
    reportType === "route"
  ) {
    return [
      {
        label:
          "Total Allocations",
        value: total,
      },

      {
        label:
          "Unique Guards",
        value:
          new Set(
            rows
              .map(
                (r) =>
                  r.guard_id
              )
              .filter(Boolean)
          ).size,
      },

      {
        label: "Routes",
        value:
          new Set(
            rows
              .map(
                (r) =>
                  r.route_id
              )
              .filter(Boolean)
          ).size,
      },

      {
        label:
          "Avg Shift Minutes",
        value:
          averageNumber(
            rows,
            "shift_duration_minutes"
          ),
      },
    ];
  }

  if (
    reportType === "place"
  ) {
    return [
      {
        label:
          "Total Allocations",
        value: total,
      },

      {
        label:
          "Unique Guards",
        value:
          new Set(
            rows
              .map(
                (r) =>
                  r.guard_id
              )
              .filter(Boolean)
          ).size,
      },

      {
        label: "Places",
        value:
          new Set(
            rows
              .map(
                (r) =>
                  r.place_id
              )
              .filter(Boolean)
          ).size,
      },

      {
        label:
          "Avg Shift Minutes",
        value:
          averageNumber(
            rows,
            "shift_duration_minutes"
          ),
      },
    ];
  }

  if (
    reportType === "escort"
  ) {
    return [
      {
        label:
          "Total Escorts",
        value: total,
      },

      {
        label:
          "Unique Guards",
        value:
          new Set(
            rows
              .map(
                (r) =>
                  r.guard_id
              )
              .filter(Boolean)
          ).size,
      },

      {
        label:
          "Total Distance KM",
        value:
          sumNumber(
            rows,
            "distance_km"
          ).toFixed(2),
      },

      {
        label:
          "Avg Duration Minutes",
        value:
          averageNumber(
            rows,
            "duration_minutes"
          ),
      },
    ];
  }

  return [
    {
      label:
        "Total Records",
      value: total,
    },

    {
      label:
        "Unique Guards",
      value:
        new Set(
          rows
            .map(
              (r) =>
                r.guard_id
            )
            .filter(Boolean)
        ).size,
    },

    {
      label:
        "Report Type",
      value:
        reportType.toUpperCase(),
    },

    {
      label: "Rows",
      value: total,
    },
  ];
}

/* =========================================================
   REPORT TYPES
========================================================= */

const REPORT_TYPES = [
  {
    key: "attendance",
    label: "Attendance",
    endpoint:
      "/api/sentinel_report/attendance",
  },

  {
    key: "qr",
    label: "QR Scans",
    endpoint:
      "/api/sentinel_report/qr-scans",
  },

  {
    key: "rfid",
    label: "RFID / Handheld",
    endpoint:
      "/api/sentinel_report/rfid-scans",
  },

  {
    key: "incidents",
    label: "Incidents",
    endpoint:
      "/api/sentinel_report/incidents",
  },

  {
    key: "alerts",
    label: "Alerts",
    endpoint:
      "/api/sentinel_report/alerts",
  },

  {
    key: "route",
    label:
      "Route Performance",
    endpoint:
      "/api/sentinel_report/route-performance",
  },

  {
    key: "place",
    label:
      "Place Performance",
    endpoint:
      "/api/sentinel_report/place-performance",
  },

  {
    key: "escort",
    label:
      "Escort Performance",
    endpoint:
      "/api/sentinel_report/escort-performance",
  },
];

const CHART_COLORS = [
  "#2563eb",
  "#16a34a",
  "#f97316",
  "#dc2626",
  "#7c3aed",
  "#0891b2",
  "#ca8a04",
  "#475569",
];

const HIDDEN_COLUMNS = [
  "has_scan_image",
  "has_media",
  "scan_datetime",

  /*
   * Do not show duplicate hash to normal users.
   * Remove this entry if you want to display it.
   */
  "duplicate_key",
];

/* =========================================================
   COMPONENT
========================================================= */

export default function SentinelReports() {
  const navigate =
    useNavigate();

  useSkin();

  const [
    guards,
    setGuards,
  ] = useState([]);

  const [
    rows,
    setRows,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    pageError,
    setPageError,
  ] = useState("");

  const [
    reportType,
    setReportType,
  ] = useState(
    "attendance"
  );

  /* Normal report filters */

  const [
    guardId,
    setGuardId,
  ] = useState("");

  /* RFID filters */

  const [
    siteCode,
    setSiteCode,
  ] = useState("");

  const [
    deviceId,
    setDeviceId,
  ] = useState("");

  const [
    cardId,
    setCardId,
  ] = useState("");

  const [
    rfidFlag,
    setRfidFlag,
  ] = useState("");

  const [
    fromDate,
    setFromDate,
  ] = useState("");

  const [
    toDate,
    setToDate,
  ] = useState("");

  const [
    dashboardData,
    setDashboardData,
  ] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const selectedReport =
    useMemo(() => {
      return (
        REPORT_TYPES.find(
          (r) =>
            r.key ===
            reportType
        ) ||
        REPORT_TYPES[0]
      );
    }, [reportType]);

  const summaryCards =
    useMemo(() => {
      return getReportSummary(
        reportType,
        rows
      );
    }, [
      reportType,
      rows,
    ]);

  const trendData =
    useMemo(
      () =>
        groupByDate(rows),
      [rows]
    );

  /* =====================================================
     NORMAL GUARD CHART
  ===================================================== */

  const guardChartData =
    useMemo(() => {
      const prepared =
        rows.map((row) => ({
          ...row,

          chart_guard:
            row.guard_name ||
            row.guard_id ||
            "Unknown",
        }));

      return groupCount(
        prepared,
        "chart_guard"
      ).slice(0, 10);
    }, [rows]);

  /* =====================================================
     RFID CHARTS
  ===================================================== */

  const rfidDeviceChartData =
    useMemo(() => {
      if (
        reportType !==
        "rfid"
      ) {
        return [];
      }

      return groupCount(
        rows,
        "device_id"
      ).slice(0, 10);
    }, [
      rows,
      reportType,
    ]);

  const rfidEventChartData =
    useMemo(() => {
      if (
        reportType !==
        "rfid"
      ) {
        return [];
      }

      return groupCount(
        rows,
        "event_type"
      );
    }, [
      rows,
      reportType,
    ]);

  const rfidSiteChartData =
    useMemo(() => {
      if (
        reportType !==
        "rfid"
      ) {
        return [];
      }

      return groupCount(
        rows,
        "site_code"
      ).slice(0, 10);
    }, [
      rows,
      reportType,
    ]);

  const severityChartData =
    useMemo(() => {
      return groupCount(
        rows,
        "severity"
      );
    }, [rows]);

  const statusChartData =
    useMemo(() => {
      if (
        reportType ===
        "alerts"
      ) {
        return groupCount(
          rows,
          "alert_status"
        );
      }

      return groupCount(
        rows,
        "status"
      );
    }, [
      rows,
      reportType,
    ]);

  const activityChartData =
    useMemo(() => {
      if (
        reportType ===
        "place"
      ) {
        return groupCount(
          rows,
          "place_name"
        ).slice(0, 10);
      }

      return groupCount(
        rows,
        "route_name"
      ).slice(0, 10);
    }, [
      rows,
      reportType,
    ]);

  const tableKeys =
    useMemo(() => {
      if (!rows.length) {
        return [];
      }

      return Object.keys(
        rows[0]
      ).filter(
        (k) =>
          !HIDDEN_COLUMNS.includes(
            k
          )
      );
    }, [rows]);

  /* =====================================================
     INITIAL LOAD
  ===================================================== */

  useEffect(() => {
    const token =
      getToken();

    const isLoggedIn =
      localStorage.getItem(
        "isLoggedIn"
      );

    if (
      !token ||
      !isLoggedIn
    ) {
      navigate("/");
      return;
    }

    fetchDashboardData(
      token
    );

    fetchGuards();
  }, []);

  /* =====================================================
     REPORT CHANGE
  ===================================================== */

  useEffect(() => {
    /*
     * Reset incompatible filters when switching reports.
     */
    if (
      reportType === "rfid"
    ) {
      setGuardId("");
    } else {
      setSiteCode("");
      setDeviceId("");
      setCardId("");
      setRfidFlag("");
    }

    fetchReport();
  }, [reportType]);

  /* =====================================================
     DASHBOARD
  ===================================================== */

  const fetchDashboardData =
    async (token) => {
      try {
        const res =
          await fetch(
            `${API_BASE_URL}/dashboard/admin/dashboard`,
            {
              headers: {
                "Content-Type":
                  "application/json",

                Authorization:
                  `Bearer ${token}`,
              },
            }
          );

        if (res.ok) {
          const data =
            await res.json();

          const companyDetails =
            data?.data
              ?.companyDetails ||
            {};

          setDashboardData(
            companyDetails
          );
        }
      } catch (error) {
        console.error(
          "Dashboard fetch error:",
          error
        );
      }
    };

  /* =====================================================
     GUARDS
  ===================================================== */

  const fetchGuards =
    async () => {
      try {
        const token =
          getToken();

        const res =
          await fetch(
            api(
              "/api/sentinel_report/guards"
            ),
            {
              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
            }
          );

        const json =
          await res.json();

        if (
          !res.ok ||
          json.success ===
            false
        ) {
          throw new Error(
            json.message ||
              "Failed to fetch guards"
          );
        }

        setGuards(
          json.data || []
        );
      } catch (err) {
        console.error(
          "Guard fetch error:",
          err
        );

        toast.error(
          "Failed to load guards"
        );

        setGuards([]);
      }
    };

  /* =====================================================
     FETCH REPORT
  ===================================================== */

  const fetchReport =
    async () => {
      try {
        setLoading(true);

        setPageError("");

        const token =
          getToken();

        const params =
          new URLSearchParams();

        /*
         * RFID uses vendor/card filters.
         */
        if (
          reportType === "rfid"
        ) {
          if (siteCode) {
            params.set(
              "siteCode",
              siteCode
            );
          }

          if (deviceId) {
            params.set(
              "deviceId",
              deviceId
            );
          }

          if (cardId) {
            params.set(
              "cardId",
              cardId
            );
          }

          if (rfidFlag) {
            params.set(
              "flag",
              rfidFlag
            );
          }
        } else {
          if (guardId) {
            params.set(
              "guardId",
              guardId
            );
          }
        }

        if (fromDate) {
          params.set(
            "fromDate",
            fromDate
          );
        }

        if (toDate) {
          params.set(
            "toDate",
            toDate
          );
        }

        const query =
          params.toString();

        const path =
          query
            ? `${selectedReport.endpoint}?${query}`
            : selectedReport.endpoint;

        const url =
          api(path);

        console.log(
          "REPORT API:",
          url
        );

        const res =
          await fetch(
            url,
            {
              headers: {
                Authorization:
                  `Bearer ${token}`,
              },
            }
          );

        const json =
          await res.json();

        if (
          !res.ok ||
          json.success ===
            false
        ) {
          throw new Error(
            json.message ||
              "Failed to fetch report"
          );
        }

        setRows(
          json.data || []
        );

        toast.success(
          `Report loaded: ${
            json.data?.length ||
            0
          } records`
        );
      } catch (err) {
        console.error(
          "Report fetch error:",
          err
        );

        setRows([]);

        setPageError(
          err.message ||
            "Failed to fetch report"
        );

        toast.error(
          err.message ||
            "Failed to fetch report"
        );
      } finally {
        setLoading(false);
      }
    };

  /* =====================================================
     SEARCH
  ===================================================== */

  const handleApplyFilters =
    () => {
      fetchReport();
    };

  /* =====================================================
     RESET
  ===================================================== */

  const handleResetFilters =
    () => {
      setGuardId("");

      setSiteCode("");
      setDeviceId("");
      setCardId("");
      setRfidFlag("");

      setFromDate("");
      setToDate("");

      setTimeout(() => {
        fetchReport();
      }, 0);
    };

  /* =====================================================
     CSV EXPORT
  ===================================================== */

  const handleExportCsv =
    () => {
      if (!rows.length) {
        toast.error(
          "No data to export"
        );

        return;
      }

      try {
        const headers =
          Object.keys(
            rows[0]
          ).filter(
            (key) =>
              !HIDDEN_COLUMNS.includes(
                key
              )
          );

        const csv = [
          headers.join(","),

          ...rows.map(
            (row) =>
              headers
                .map(
                  (key) => {
                    const value =
                      row[key] ===
                        null ||
                      row[key] ===
                        undefined
                        ? ""
                        : String(
                            row[
                              key
                            ]
                          );

                    return `"${value.replace(
                      /"/g,
                      '""'
                    )}"`;
                  }
                )
                .join(",")
          ),
        ].join("\n");

        const blob =
          new Blob(
            [csv],
            {
              type:
                "text/csv;charset=utf-8;",
            }
          );

        const url =
          URL.createObjectURL(
            blob
          );

        const link =
          document.createElement(
            "a"
          );

        link.href =
          url;

        link.download =
          `${
            selectedReport.key
          }-report-${new Date()
            .toISOString()
            .slice(
              0,
              10
            )}.csv`;

        document.body.appendChild(
          link
        );

        link.click();

        document.body.removeChild(
          link
        );

        URL.revokeObjectURL(
          url
        );

        toast.success(
          `CSV exported successfully! (${rows.length} records)`
        );
      } catch (err) {
        console.error(
          "CSV export error:",
          err
        );

        toast.error(
          "Failed to export CSV"
        );
      }
    };

  const routePage =
    useMemo(
      () =>
        dashboardData.user_map ===
        "google"
          ? "/route-master"
          : "/route-master1",
      [
        dashboardData.user_map,
      ]
    );

  return (
    <Layout
      dashboardData={
        dashboardData
      }
      routePage={
        routePage
      }
      activePage="reports"
      activeMenu="reports"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,

          style: {
            background:
              "var(--card-bg)",

            color:
              "var(--text-primary)",

            border:
              "1px solid var(--card-border)",

            borderRadius:
              "12px",

            padding:
              "16px 20px",

            fontSize:
              "13px",

            fontWeight:
              "600",

            boxShadow:
              "0 8px 32px rgba(0,0,0,0.3)",
          },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">

        {/* HEADER */}

        <div className="flex items-center justify-between gap-3 flex-wrap mb-[22px]">

          <div>

            <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
              Sentinel Reports
            </h1>

            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
              Attendance, QR, handheld RFID,
              incidents, alerts, route, place
              and escort reports.
            </p>

          </div>

          <button
            type="button"
            onClick={() =>
              navigate(
                "/dashboard"
              )
            }
            className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold"
          >
            Dashboard
          </button>

        </div>

        {pageError && (
          <div className="mb-4 px-4 py-3 rounded-xl text-[13px] font-bold border bg-[var(--red-faint)] border-[var(--red-border)] text-[var(--red)]">
            {pageError}
          </div>
        )}

        {/* =====================================================
            FILTERS
        ===================================================== */}

        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">

          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />

          <div
            className={`grid grid-cols-1 gap-4 ${
              reportType === "rfid"
                ? "md:grid-cols-4 lg:grid-cols-7"
                : "md:grid-cols-5"
            }`}
          >

            {/* REPORT TYPE */}

            <div>

              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                Report Type
              </label>

              <select
                value={
                  reportType
                }
                onChange={(e) =>
                  setReportType(
                    e.target.value
                  )
                }
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px]"
              >

                {REPORT_TYPES.map(
                  (r) => (
                    <option
                      key={
                        r.key
                      }
                      value={
                        r.key
                      }
                    >
                      {r.label}
                    </option>
                  )
                )}

              </select>

            </div>

            {/* =================================================
                RFID FILTERS
            ================================================= */}

            {reportType ===
              "rfid" ? (
              <>

                <div>
                  <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    Site Code
                  </label>

                  <input
                    type="text"
                    value={
                      siteCode
                    }
                    onChange={(e) =>
                      setSiteCode(
                        e.target.value
                      )
                    }
                    placeholder="SITE001"
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px]"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    Device ID
                  </label>

                  <input
                    type="text"
                    value={
                      deviceId
                    }
                    onChange={(e) =>
                      setDeviceId(
                        e.target.value
                      )
                    }
                    placeholder="01"
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px]"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    Card ID
                  </label>

                  <input
                    type="text"
                    value={
                      cardId
                    }
                    onChange={(e) =>
                      setCardId(
                        e.target.value
                      )
                    }
                    placeholder="00125"
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px]"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    Flag
                  </label>

                  <select
                    value={
                      rfidFlag
                    }
                    onChange={(e) =>
                      setRfidFlag(
                        e.target.value
                      )
                    }
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px]"
                  >

                    <option value="">
                      All Flags
                    </option>

                    <option value="0S">
                      0S - Round Start
                    </option>

                    <option value="0T">
                      0T - Checkpoint
                    </option>

                    <option value="0E">
                      0E - Round End
                    </option>

                    {Array.from(
                      {
                        length: 15,
                      },
                      (_, i) => {
                        const value =
                          String(
                            i + 1
                          ).padStart(
                            2,
                            "0"
                          );

                        return (
                          <option
                            key={
                              value
                            }
                            value={
                              value
                            }
                          >
                            {value}
                            {" - "}
                            Anomaly
                          </option>
                        );
                      }
                    )}

                  </select>
                </div>

              </>
            ) : (
              /* NORMAL GUARD FILTER */
              <div>

                <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Guard
                </label>

                <select
                  value={
                    guardId
                  }
                  onChange={(e) =>
                    setGuardId(
                      e.target.value
                    )
                  }
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px]"
                >

                  <option value="">
                    All Guards
                  </option>

                  {guards.map(
                    (g) => {
                      const id =
                        g.fld_memberId ||
                        g.member_id ||
                        g.guard_id;

                      const name =
                        g.fld_name ||
                        g.fullName ||
                        g.guard_name ||
                        g.fld_username;

                      return (
                        <option
                          key={
                            id
                          }
                          value={
                            id
                          }
                        >
                          {name} ({id})
                        </option>
                      );
                    }
                  )}

                </select>

              </div>
            )}

            {/* FROM DATE */}

            <div>

              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                From Date
              </label>

              <input
                type="date"
                value={
                  fromDate
                }
                onChange={(e) =>
                  setFromDate(
                    e.target.value
                  )
                }
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px]"
              />

            </div>

            {/* TO DATE */}

            <div>

              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                To Date
              </label>

              <input
                type="date"
                value={
                  toDate
                }
                onChange={(e) =>
                  setToDate(
                    e.target.value
                  )
                }
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px]"
              />

            </div>

          </div>

          <div className="flex flex-wrap gap-2 mt-4 justify-end">

            <button
              type="button"
              onClick={
                handleApplyFilters
              }
              className="border-0 rounded-xl px-5 py-3 text-[13px] font-extrabold cursor-pointer"
              style={{
                background:
                  "var(--btn-bg)",
                color:
                  "var(--btn-text)",
              }}
            >
              Search
            </button>

            <button
              type="button"
              onClick={
                handleResetFilters
              }
              className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-5 py-3 text-[13px] font-extrabold"
            >
              Reset
            </button>

            <button
              type="button"
              onClick={
                handleExportCsv
              }
              className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-5 py-3 text-[13px] font-extrabold"
            >
              Export
            </button>

          </div>

        </div>

        {/* SUMMARY CARDS */}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-[18px]">

          {summaryCards.map(
            (card) => (
              <div
                key={
                  card.label
                }
                className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-4 py-4 shadow-[0_8px_32px_rgba(0,0,0,0.3)]"
              >

                <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-muted)] m-0">
                  {card.label}
                </p>

                <h2 className="text-[26px] font-extrabold text-[var(--accent)] mt-1 m-0">
                  {card.value}
                </h2>

              </div>
            )
          )}

        </div>

        {/* =====================================================
            DAILY TREND + NORMAL GUARD CHART
        ===================================================== */}

        <div
          className={`grid grid-cols-1 ${
            reportType !== "rfid"
              ? "lg:grid-cols-2"
              : ""
          } gap-4 mb-4`}
        >

          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-6 py-5">

            <h3 className="text-[17px] font-extrabold m-0">
              Daily Report Trend
            </h3>

            <p className="text-[12px] text-[var(--text-muted)] mb-4 mt-1">
              Report activity by date.
            </p>

            <div className="h-72">

              <ResponsiveContainer
                width="100%"
                height="100%"
              >

                <LineChart
                  data={
                    trendData
                  }
                >

                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--divider)"
                  />

                  <XAxis
                    dataKey="date"
                  />

                  <YAxis
                    allowDecimals={
                      false
                    }
                  />

                  <Tooltip />

                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#2563eb"
                    strokeWidth={
                      3
                    }
                  />

                </LineChart>

              </ResponsiveContainer>

            </div>

          </div>

          {reportType !==
            "rfid" && (

            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-6 py-5">

              <h3 className="text-[17px] font-extrabold m-0">
                Top Guards by Activity
              </h3>

              <div className="h-72">

                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >

                  <BarChart
                    data={
                      guardChartData
                    }
                  >

                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={
                        false
                      }
                    />

                    <XAxis
                      dataKey="name"
                      hide
                    />

                    <YAxis
                      allowDecimals={
                        false
                      }
                    />

                    <Tooltip />

                    <Bar
                      dataKey="value"
                      fill="#2563eb"
                    />

                  </BarChart>

                </ResponsiveContainer>

              </div>

            </div>
          )}

        </div>

        {/* =====================================================
            RFID CHARTS
        ===================================================== */}

        {reportType ===
          "rfid" && (

          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">

              <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-6 py-5">

                <h3 className="text-[17px] font-extrabold m-0">
                  Device Activity
                </h3>

                <p className="text-[12px] text-[var(--text-muted)] mb-4 mt-1">
                  Transactions received from each handheld device.
                </p>

                <div className="h-72">

                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >

                    <BarChart
                      data={
                        rfidDeviceChartData
                      }
                    >

                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={
                          false
                        }
                      />

                      <XAxis
                        dataKey="name"
                      />

                      <YAxis
                        allowDecimals={
                          false
                        }
                      />

                      <Tooltip />

                      <Bar
                        dataKey="value"
                        fill="#7c3aed"
                      />

                    </BarChart>

                  </ResponsiveContainer>

                </div>

              </div>

              <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-6 py-5">

                <h3 className="text-[17px] font-extrabold m-0">
                  Event Breakdown
                </h3>

                <p className="text-[12px] text-[var(--text-muted)] mb-4 mt-1">
                  Round start, checkpoint, anomaly and round end records.
                </p>

                <div className="h-72">

                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >

                    <PieChart>

                      <Pie
                        data={
                          rfidEventChartData
                        }
                        dataKey="value"
                        nameKey="name"
                        outerRadius={
                          95
                        }
                        label={({
                          name,
                          percent,
                        }) =>
                          `${name} ${(
                            percent *
                            100
                          ).toFixed(
                            0
                          )}%`
                        }
                      >

                        {rfidEventChartData.map(
                          (
                            entry,
                            index
                          ) => (
                            <Cell
                              key={
                                entry.name
                              }
                              fill={
                                CHART_COLORS[
                                  index %
                                    CHART_COLORS.length
                                ]
                              }
                            />
                          )
                        )}

                      </Pie>

                      <Tooltip />

                    </PieChart>

                  </ResponsiveContainer>

                </div>

              </div>

            </div>

            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-6 py-5 mb-4">

              <h3 className="text-[17px] font-extrabold m-0">
                Site Activity
              </h3>

              <p className="text-[12px] text-[var(--text-muted)] mb-4 mt-1">
                RFID records received for each Dani site.
              </p>

              <div className="h-72">

                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >

                  <BarChart
                    data={
                      rfidSiteChartData
                    }
                  >

                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={
                        false
                      }
                    />

                    <XAxis
                      dataKey="name"
                    />

                    <YAxis
                      allowDecimals={
                        false
                      }
                    />

                    <Tooltip />

                    <Bar
                      dataKey="value"
                      fill="#16a34a"
                    />

                  </BarChart>

                </ResponsiveContainer>

              </div>

            </div>
          </>
        )}

        {/* =====================================================
            INCIDENT / ALERT CHARTS
        ===================================================== */}

        {(reportType ===
          "alerts" ||
          reportType ===
            "incidents") && (

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">

            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-6 py-5">

              <h3 className="text-[17px] font-extrabold">
                Severity Breakdown
              </h3>

              <div className="h-72">

                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >

                  <PieChart>

                    <Pie
                      data={
                        severityChartData
                      }
                      dataKey="value"
                      nameKey="name"
                      outerRadius={
                        95
                      }
                    >

                      {severityChartData.map(
                        (
                          entry,
                          index
                        ) => (
                          <Cell
                            key={
                              entry.name
                            }
                            fill={
                              CHART_COLORS[
                                index %
                                  CHART_COLORS.length
                              ]
                            }
                          />
                        )
                      )}

                    </Pie>

                    <Tooltip />

                  </PieChart>

                </ResponsiveContainer>

              </div>

            </div>

            <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-6 py-5">

              <h3 className="text-[17px] font-extrabold">
                Status Breakdown
              </h3>

              <div className="h-72">

                <ResponsiveContainer
                  width="100%"
                  height="100%"
                >

                  <PieChart>

                    <Pie
                      data={
                        statusChartData
                      }
                      dataKey="value"
                      nameKey="name"
                      outerRadius={
                        95
                      }
                    >

                      {statusChartData.map(
                        (
                          entry,
                          index
                        ) => (
                          <Cell
                            key={
                              entry.name
                            }
                            fill={
                              CHART_COLORS[
                                index %
                                  CHART_COLORS.length
                              ]
                            }
                          />
                        )
                      )}

                    </Pie>

                    <Tooltip />

                  </PieChart>

                </ResponsiveContainer>

              </div>

            </div>

          </div>
        )}

        {/* ROUTE / PLACE */}

        {(reportType ===
          "qr" ||
          reportType ===
            "route" ||
          reportType ===
            "place") && (

          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-6 py-5 mb-4">

            <h3 className="text-[17px] font-extrabold">
              {reportType ===
              "place"
                ? "Place Activity"
                : "Route Activity"}
            </h3>

            <div className="h-72">

              <ResponsiveContainer
                width="100%"
                height="100%"
              >

                <BarChart
                  data={
                    activityChartData
                  }
                >

                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={
                      false
                    }
                  />

                  <XAxis
                    dataKey="name"
                  />

                  <YAxis
                    allowDecimals={
                      false
                    }
                  />

                  <Tooltip />

                  <Bar
                    dataKey="value"
                    fill={
                      reportType ===
                      "place"
                        ? "#7c3aed"
                        : "#16a34a"
                    }
                  />

                </BarChart>

              </ResponsiveContainer>

            </div>

          </div>
        )}

        {/* =====================================================
            DATA TABLE
        ===================================================== */}

        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.3)]">

          <div className="px-6 py-4 border-b border-[var(--divider)] flex items-center justify-between">

            <div>

              <h3 className="text-[17px] font-extrabold m-0">
                {selectedReport.label}
                {" "}
                Detailed Report
              </h3>

              <p className="text-[12px] text-[var(--text-muted)] mt-1">
                Complete report details for audit and export.
              </p>

            </div>

            <span className="text-[13px] text-[var(--text-muted)]">
              {rows.length}
              {" "}
              records
            </span>

          </div>

          <div className="overflow-x-auto">

            {loading ? (

              <div className="flex items-center justify-center py-12 gap-3">

                <div className="w-8 h-8 border-4 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />

                <span>
                  Loading report...
                </span>

              </div>

            ) : rows.length ===
              0 ? (

              <div className="text-center py-12 text-[var(--text-muted)]">
                No data found
              </div>

            ) : (

              <table className="w-full text-[13px]">

                <thead>

                  <tr
                    style={{
                      background:
                        "var(--btn-bg)",
                    }}
                  >

                    {tableKeys.map(
                      (key) => (

                        <th
                          key={
                            key
                          }
                          className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white whitespace-nowrap"
                        >
                          {key
                            .replace(
                              /_/g,
                              " "
                            )
                            .toUpperCase()}
                        </th>

                      )
                    )}

                  </tr>

                </thead>

                <tbody>

                  {rows.map(
                    (
                      row,
                      index
                    ) => (

                      <tr
                        key={
                          row.id ||
                          row.run_id ||
                          row.allocation_id ||
                          index
                        }
                        className="border-b border-[var(--divider)] hover:bg-[var(--accent-faint)]"
                      >

                        {tableKeys.map(
                          (key) => {

                            const val =
                              row[key];

                            if (
                              isMediaField(
                                key
                              )
                            ) {

                              const url =
                                getMediaUrl(
                                  val
                                );

                              if (
                                !url
                              ) {
                                return (
                                  <td
                                    key={
                                      key
                                    }
                                    className="px-4 py-3 text-[var(--text-muted)]"
                                  >
                                    No media
                                  </td>
                                );
                              }

                              return (
                                <td
                                  key={
                                    key
                                  }
                                  className="px-4 py-3"
                                >

                                  <div className="flex items-center gap-2">

                                    {isImageUrl(
                                      url
                                    ) && (

                                      <a
                                        href={
                                          url
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >

                                        <img
                                          src={
                                            url
                                          }
                                          alt="media"
                                          className="w-12 h-12 object-cover rounded-lg"
                                        />

                                      </a>

                                    )}

                                    <a
                                      href={
                                        url
                                      }
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-[var(--accent)] font-bold underline"
                                    >
                                      View
                                    </a>

                                  </div>

                                </td>
                              );
                            }

                            return (
                              <td
                                key={
                                  key
                                }
                                className="px-4 py-3 whitespace-nowrap text-[var(--text-primary)]"
                              >
                                {formatValue(
                                  val,
                                  key
                                )}
                              </td>
                            );

                          }
                        )}

                      </tr>

                    )
                  )}

                </tbody>

              </table>

            )}

          </div>

        </div>

      </div>

    </Layout>
  );
}